import React from 'react';
import { useReducedMotion } from 'framer-motion';
import { motion } from '../../lib/motionShim';
import { Link } from 'react-router-dom';
import { Instagram, Mail } from 'lucide-react';
import { StonLogoFull } from '../StonLogo';
import StoreBadges from '../StoreBadges';

const footerLinks = [
  {
    label: 'Product',
    links: [
      { title: 'Features', href: '/product' },
      { title: 'Free Tools', href: '/#tools' },
      { title: 'How It Works', href: '/product' },
      { title: 'Download App', href: '/#waitlist' },
    ],
  },
  {
    label: 'Company',
    links: [
      { title: 'Our Story', href: '/#story' },
      { title: 'Results', href: '/#proof' },
      { title: 'Careers', href: 'mailto:founder@ston.fit?subject=Careers%20at%20STON.FIT' },
      { title: 'Contact', href: 'mailto:founder@ston.fit' },
    ],
  },
  {
    label: 'Legal',
    links: [
      { title: 'Privacy Policy', href: '/privacy' },
      { title: 'Terms of Service', href: '/terms' },
      { title: 'Support', href: 'mailto:founder@ston.fit?subject=Support' },
      { title: 'FAQs', href: '/#faq' },
    ],
  },
  {
    label: 'Connect',
    links: [
      { title: 'Instagram', href: 'https://www.instagram.com/ston.fit/', icon: Instagram },
      { title: 'Email', href: 'mailto:founder@ston.fit', icon: Mail },
    ],
  },
];

function AnimatedContainer({ className, delay = 0.1, children }) {
  const shouldReduceMotion = useReducedMotion();

  if (shouldReduceMotion) {
    return <div className={className}>{children}</div>;
  }

  return (
    <motion.div
      initial={{ translateY: -8, opacity: 0 }}
      whileInView={{ translateY: 0, opacity: 1 }}
      viewport={{ once: true }}
      transition={{ delay, duration: 0.8 }}
      className={className}
    >
      {children}
    </motion.div>
  );
}

export function FooterSection() {
  return (
    <footer 
      className="relative w-full max-w-[1400px] mx-auto flex flex-col items-center justify-center rounded-t-3xl md:rounded-t-[48px] border-t border-ston-border bg-ston-bg px-6 py-12 lg:py-16"
      style={{
        background: 'radial-gradient(35% 128px at 50% 0%, rgba(147, 51, 234, 0.08), transparent)',
      }}
      data-testid="footer-section"
    >
      {/* Top glow line */}
      <div className="absolute top-0 left-1/2 -translate-x-1/2 -translate-y-1/2 h-px w-1/3 rounded-full blur-sm bg-ston-purple/30" />

      <div className="grid w-full gap-8 xl:grid-cols-3 xl:gap-8">
        {/* Brand Section */}
        <AnimatedContainer className="space-y-4">
          <StonLogoFull logoSize={32} />
          <p className="mt-4 text-sm text-ston-text-secondary leading-relaxed max-w-[280px]">
            From tracking to understanding — for people who want to be healthy,
            and the professionals who guide them.
          </p>
          <StoreBadges height={40} align="start" className="!justify-start" />

          <p className="text-sm text-ston-text-muted">
            Contact: <a href="mailto:founder@ston.fit" className="hover:text-ston-text transition-colors">founder@ston.fit</a>
          </p>
          <p className="text-sm text-ston-text-muted">
            © {new Date().getFullYear()} STONFIT Healthtech Pvt. Ltd.
          </p>
        </AnimatedContainer>

        {/* Links Grid */}
        <div className="mt-10 grid grid-cols-2 gap-8 md:grid-cols-4 xl:col-span-2 xl:mt-0">
          {footerLinks.map((section, index) => (
            <AnimatedContainer key={section.label} delay={0.1 + index * 0.1}>
              <div className="mb-10 md:mb-0">
                <h3 className="text-xs font-semibold uppercase tracking-wider text-ston-text-secondary">
                  {section.label}
                </h3>
                <ul className="mt-4 space-y-2">
                  {section.links.map((link) => (
                    <li key={link.title}>
                      {link.href.startsWith('/') || link.href.startsWith('#') ? (
                        <Link
                          to={link.href}
                          className="inline-flex items-center text-sm text-ston-text-muted hover:text-ston-text transition-colors duration-300"
                          data-testid={`footer-link-${link.title.toLowerCase().replace(/\s/g, '-')}`}
                        >
                          {link.icon && <link.icon className="mr-2 h-4 w-4" />}
                          {link.title}
                        </Link>
                      ) : (
                        <a
                          href={link.href}
                          target={link.href.startsWith('http') ? '_blank' : undefined}
                          rel={link.href.startsWith('http') ? 'noopener noreferrer' : undefined}
                          className="inline-flex items-center text-sm text-ston-text-muted hover:text-ston-text transition-colors duration-300"
                          data-testid={`footer-link-${link.title.toLowerCase().replace(/\s/g, '-')}`}
                        >
                          {link.icon && <link.icon className="mr-2 h-4 w-4" />}
                          {link.title}
                        </a>
                      )}
                    </li>
                  ))}
                </ul>
              </div>
            </AnimatedContainer>
          ))}
        </div>
      </div>
    </footer>
  );
}

export default FooterSection;
